import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  CardMedia,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { apiRequest, api } from '../../utils/api';
import PDFViewerWithComments from '../../components/PDFViewerWithComments';
import CommentDialog from '../../components/CommentDialog';
import { User } from '../../types';
import { isTokenValid } from '../../utils/auth';

interface FileComment {
  id: number;
  fileId: number;
  pageNumber: number;
  xPosition: number;
  yPosition: number;
  commentText: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  creatorName?: string | null;
}

interface JobFile {
  id: number;
  filename: string;
  originalName: string;
  customName?: string | null;
  apartment?: string | null;
  filePath?: string;
  fileSize: number;
  mimeType: string;
  isDrawing?: boolean;
  createdAt: string;
  uploadedBy?: number;
}

interface FilesProps {
  jobId: number;
  asTab?: boolean;
  isActive?: boolean;
}

export default function Files({ jobId, asTab = false, isActive = false }: FilesProps) {
  const [files, setFiles] = useState<JobFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<JobFile | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [previewUrls, setPreviewUrls] = useState<Map<number, string>>(new Map());
  const [loadingPreviews, setLoadingPreviews] = useState<Set<number>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<JobFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedComment, setSelectedComment] = useState<FileComment | null>(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentRefreshTrigger, setCommentRefreshTrigger] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // File upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [fileObject, setFileObject] = useState<globalThis.File | null>(null);
  const [customFileName, setCustomFileName] = useState('');
  const [selectedApartment, setSelectedApartment] = useState<string | null>(null);
  const [availableApartments, setAvailableApartments] = useState<string[]>([]);
  const [jobApartment, setJobApartment] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Process file (validation and open modal) - defined early so it can be used in useEffect
  const processFile = useCallback((file: File) => {
    // Validate file type (PDF, image, or video)
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'video/mp4',
      'video/mpeg',
      'video/quicktime',
      'video/x-msvideo',
      'video/x-ms-wmv',
      'video/webm',
    ];

    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: 'Invalid file type. Only PDFs, images, and videos are allowed.',
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (max 2GB for videos and large files)
    if (file.size > 2 * 1024 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'File size must be less than 2GB',
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Store the file and open the modal
    setFileObject(file);
    setCustomFileName(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for custom name
    setSelectedApartment(null);
    setIsDrawing(false);
    setUploadModalOpen(true);
  }, []);

  useEffect(() => {
    // Only load if component is active, we have a valid jobId, and token is valid
    if ((isActive || !asTab) && jobId && jobId > 0) {
      // Check if token is valid before making API calls
      if (!isTokenValid()) {
        console.error('Token is not valid, skipping API calls');
        setSnackbar({
          open: true,
          message: 'Your session has expired. Please refresh the page.',
        });
        return;
      }

      loadFiles().catch((error) => {
        console.error('Error in loadFiles:', error);
        // Don't let errors redirect - just show error message
        if (error.response?.status !== 401) {
          setSnackbar({
            open: true,
            message: 'Failed to load files. Please try again.',
          });
        }
      });
      loadJobApartments().catch((error) => {
        console.error('Error in loadJobApartments:', error);
      });
    }
  }, [jobId, isActive, asTab]);

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        setCurrentUser(response.data as User);
      } catch (error: any) {
        console.error('Error loading current user:', error);
        // Don't redirect on error - just log it
        // The API interceptor will handle 401 errors globally
      }
    };
    if (isActive || !asTab) {
      loadCurrentUser();
    }
  }, [isActive, asTab]);

  // Add paste event listener when component is active
  useEffect(() => {
    if (!isActive && asTab) return;

    const handlePaste = (e: ClipboardEvent) => {
      // Don't intercept paste if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      // Look for image in clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            // Convert blob to File object
            const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
            processFile(file);
          }
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [isActive, asTab, processFile]);

  const loadJobApartments = async () => {
    if (!jobId || jobId <= 0) {
      return;
    }

    try {
      const response = await apiRequest(`/jobs/${jobId}`, { method: 'GET' });
      const job = response.data;
      const apartment = job.apartment || '';
      setJobApartment(apartment);
      
      // Parse comma-separated apartments and sort them
      const apartments = apartment
        ? apartment.split(',').map((a: string) => a.trim()).filter((a: string) => a).sort()
        : [];
      setAvailableApartments(apartments);
    } catch (error) {
      console.error('Error loading job apartments:', error);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
      // Cleanup all preview URLs
      previewUrls.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [fileUrl, previewUrls]);

  const loadPreview = async (file: JobFile) => {
    if (previewUrls.has(file.id) || !file.mimeType?.startsWith('image/') || loadingPreviews.has(file.id)) {
      return;
    }

    try {
      setLoadingPreviews((prev) => {
        const newSet = new Set(prev);
        newSet.add(file.id);
        return newSet;
      });

      const encodedFilename = encodeURIComponent(file.filename);
      const previewResponse = await api.get(`/jobs/${jobId}/files/${encodedFilename}`, {
        params: { originalName: file.originalName },
        responseType: 'blob',
      });
      const blob = new Blob([previewResponse.data], { type: file.mimeType });
      const blobUrl = URL.createObjectURL(blob);
      
      setPreviewUrls((prev) => {
        const newMap = new Map(prev);
        newMap.set(file.id, blobUrl);
        return newMap;
      });
    } catch (error) {
      console.error(`Error loading preview for file ${file.id}:`, error);
    } finally {
      setLoadingPreviews((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file.id);
        return newSet;
      });
    }
  };

  const loadFiles = async () => {
    if (!jobId || jobId <= 0) {
      console.error('Invalid jobId:', jobId);
      return;
    }

    try {
      setLoading(true);
      const response = await apiRequest(`/jobs/${jobId}/files`, { method: 'GET' });
      const loadedFiles = response.data?.files || [];
      
      // Sort files by apartment (files without apartment go to the end)
      const sortedFiles = [...loadedFiles].sort((a, b) => {
        // Files without apartment go to the end
        if (!a.apartment && !b.apartment) return 0;
        if (!a.apartment) return 1;
        if (!b.apartment) return -1;
        // Sort alphabetically by apartment
        return a.apartment.localeCompare(b.apartment);
      });
      
      setFiles(sortedFiles);
      
      // Cleanup old preview URLs
      previewUrls.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      setPreviewUrls(new Map());
      setLoadingPreviews(new Set());
      
      // Load previews for images in the background (don't wait)
      const imageFiles = loadedFiles.filter((file: JobFile) => file.mimeType?.startsWith('image/'));
      imageFiles.forEach((file: JobFile) => {
        loadPreview(file).catch((error) => {
          console.error(`Failed to load preview for file ${file.id}:`, error);
        });
      });
    } catch (error) {
      console.error('Error loading files:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load files',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone container
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      // Process the first file
      processFile(droppedFiles[0]);
    }
  };

  const handleUpload = async () => {
    if (!fileObject) return;

    try {
      setUploading(true);
      setUploadModalOpen(false);
      
      const formData = new FormData();
      formData.append('file', fileObject);
      if (customFileName.trim()) {
        formData.append('customName', customFileName.trim());
      }
      if (selectedApartment) {
        formData.append('apartment', selectedApartment);
      }
      formData.append('isDrawing', isDrawing.toString());

      await api.post(`/jobs/${jobId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0, // No timeout — needed for large (2 GB) file uploads
      });

      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
      });
      
      // Clear file input and modal state
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFileObject(null);
      setCustomFileName('');
      setSelectedApartment(null);
      setIsDrawing(false);

      // Reload files list
      loadFiles();
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload file';
      setSnackbar({
        open: true,
        message: errorMessage,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Reopen modal on error so user can try again
      setUploadModalOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setUploadModalOpen(false);
    setFileObject(null);
    setCustomFileName('');
    setSelectedApartment(null);
    setIsDrawing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileClick = async (file: JobFile) => {
    try {
      setLoadingFile(true);
      setViewingFile(file);
      setViewerOpen(true);

      const encodedFilename = encodeURIComponent(file.filename);

      // Videos: use a direct URL so the browser can stream with Range requests
      // (seeking, partial loading). The httpOnly auth cookie is sent automatically
      // on same-origin requests via the Vite proxy.
      if (file.mimeType?.startsWith('video/')) {
        setFileUrl(`/api/jobs/${jobId}/files/${encodedFilename}`);
        setLoadingFile(false);
        return;
      }

      // PDF and images: fetch as blob so the auth cookie is included
      setFileUrl(`/api/jobs/${jobId}/files/${encodedFilename}?originalName=${encodeURIComponent(file.originalName)}`);
      // const response = await api.get(`/jobs/${jobId}/files/${encodedFilename}`, {
      //   params: { originalName: file.originalName },
      //   // responseType: 'blob',
      // });
      // console.log('[Files] blob response status:', response.status, 'content-type:', response.headers['content-type'], 'size:', response.data?.size);
      // const blob = new Blob([response.data], { type: file.mimeType || 'application/octet-stream' });
      // console.log('[Files] created blob size:', blob.size, 'type:', blob.type);
      // const blobUrl = URL.createObjectURL(blob);
      // console.log('[Files] blobUrl:', blobUrl);
      // setFileUrl(blobUrl);
    } catch (error) {
      console.error('Error loading file:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load file',
      });
      setViewerOpen(false);
      setViewingFile(null);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleDownload = (e: React.MouseEvent, file: JobFile) => {
    e.stopPropagation();
    const encodedFilename = encodeURIComponent(file.filename);
    const actualExt = file.filename.includes('.') ? '.' + file.filename.split('.').pop() : '';
    const filenameStem = file.filename.replace(/\.[^/.]+$/, '');
    const baseName = (file.customName || filenameStem).replace(/\.[^/.]+$/, '');
    const downloadName = baseName + actualExt;
    // Use a direct link so the browser streams the download natively — no need to
    // buffer the entire file in memory first. The httpOnly auth cookie is sent
    // automatically on same-origin requests, so no token is exposed in the URL.
    const link = document.createElement('a');
    link.href = `/api/jobs/${jobId}/files/${encodedFilename}?download=1`;
    link.download = downloadName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseViewer = () => {
    // Revoke blob URL to free memory
    if (fileUrl && fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileUrl);
    }
    setViewerOpen(false);
    setViewingFile(null);
    setFileUrl('');
  };

  const handleDeleteClick = (e: React.MouseEvent, file: JobFile) => {
    e.stopPropagation();
    // Check if user is a Field Worker
    const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot delete files' });
      return;
    }
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    // Check if user is a Field Worker
    const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot delete files' });
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      return;
    }

    try {
      setDeleting(true);
      await apiRequest(`/jobs/${jobId}/files/${fileToDelete.id}`, { method: 'DELETE' });
      
      // Cleanup preview URL if it exists
      if (previewUrls.has(fileToDelete.id)) {
        const previewUrl = previewUrls.get(fileToDelete.id);
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrls((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileToDelete.id);
          return newMap;
        });
      }

      setSnackbar({
        open: true,
        message: 'File deleted successfully',
      });
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      loadFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete file',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFileToDelete(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box
      ref={dropZoneRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{ position: 'relative' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Files</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mpeg,.mov,.avi,.wmv,.webm,application/pdf,image/*,video/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
          <IconButton onClick={loadFiles} disabled={loading || uploading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Drop Zone Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            pointerEvents: 'none',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main' }} />
          <Typography variant="h5" color="primary.main" fontWeight="bold">
            Drop files here to upload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can also paste images directly (Ctrl+V / Cmd+V)
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="80px">Preview</TableCell>
                  <TableCell>File Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                          No files uploaded yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Drag and drop files here, or paste images directly (Ctrl+V / Cmd+V)
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  files.map((file) => (
                    <TableRow
                      key={file.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => handleFileClick(file)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {file.mimeType?.startsWith('image/') ? (
                          loadingPreviews.has(file.id) ? (
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <CircularProgress size={20} />
                            </Box>
                          ) : previewUrls.has(file.id) ? (
                            <Box
                              component="img"
                              src={previewUrls.get(file.id)}
                              alt={file.originalName}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                              }}
                              onClick={() => handleFileClick(file)}
                              onError={() => {
                                // If image fails to load, remove from preview URLs
                                setPreviewUrls((prev) => {
                                  const newMap = new Map(prev);
                                  newMap.delete(file.id);
                                  return newMap;
                                });
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                No preview
                              </Typography>
                            </Box>
                          )
                        ) : file.mimeType === 'application/pdf' ? (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'error.light',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'error.main',
                              },
                            }}
                            onClick={() => handleFileClick(file)}
                          >
                            <PictureAsPdfIcon sx={{ color: 'white', fontSize: 30 }} />
                          </Box>
                        ) : file.mimeType?.startsWith('video/') ? (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'primary.light',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'primary.main',
                              },
                            }}
                            onClick={() => handleFileClick(file)}
                          >
                            <VideoLibraryIcon sx={{ color: 'white', fontSize: 30 }} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.200',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              File
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 500,
                          }}
                        >
                          {file.customName || file.originalName}
                        </Typography>
                        {file.apartment && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Apartment: {file.apartment}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(file.fileSize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {file.mimeType || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(file.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDownload(e, file)}
                          title="Download"
                        >
                          <DownloadIcon />
                        </IconButton>
                        {currentUser?.role === 'SYSTEM ADMIN' && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, file)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Upload Loading Modal */}
      <Backdrop
        open={uploading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">Uploading file...</Typography>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
          Please wait while your file is being uploaded
        </Typography>
      </Backdrop>

      {/* File Viewer Modal */}
      <Dialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component="span" sx={{ flex: 1, pr: 2 }}>
            {viewingFile?.customName || viewingFile?.originalName}
          </Box>
          <Box>
            {viewingFile && (
              <IconButton
                size="small"
                onClick={(e) => handleDownload(e, viewingFile)}
                title="Download"
                sx={{ mr: 1 }}
              >
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton size="small" onClick={handleCloseViewer} title="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'grey.100',
            overflow: 'auto',
          }}
        >
          {loadingFile ? (
            <Box
              sx={{
                width: '100%',
                height: 'calc(90vh - 120px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                Loading file...
              </Typography>
            </Box>
          ) : viewingFile && fileUrl ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
              }}
            >
              {viewingFile.mimeType?.startsWith('image/') ? (
                <img
                  src={fileUrl}
                  alt={viewingFile.originalName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 120px)',
                    objectFit: 'contain',
                  }}
                />
              ) : viewingFile.mimeType === 'application/pdf' ? (
                <Box sx={{ width: '100%', height: 'calc(90vh - 120px)' }}>
                  <PDFViewerWithComments
                    fileUrl={fileUrl}
                    fileId={viewingFile.id}
                    jobId={jobId}
                    onCommentClick={(comment) => {
                      setSelectedComment(comment);
                      setCommentDialogOpen(true);
                    }}
                    currentUserId={currentUser?.id}
                    refreshTrigger={commentRefreshTrigger}
                  />
                </Box>
              ) : viewingFile.mimeType?.startsWith('video/') ? (
                <Box
                  sx={{
                    width: '100%',
                    height: 'calc(90vh - 120px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'black',
                  }}
                >
                  <CardMedia
                    component="video"
                    src={fileUrl}
                    controls
                    autoPlay
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Preview not available for this file type
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={(e) => viewingFile && handleDownload(e, viewingFile)}
                    sx={{ mt: 2 }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewer}>Close</Button>
          {viewingFile && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={(e) => handleDownload(e, viewingFile)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* File Upload Modal */}
      <Dialog
        open={uploadModalOpen}
        onClose={handleCancelUpload}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="File Name"
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value)}
              fullWidth
              required
              helperText="Enter a custom name for this file (defaults to original filename if empty)"
            />
            {availableApartments.length > 0 ? (
              <Autocomplete
                options={availableApartments}
                value={selectedApartment}
                onChange={(_, newValue) => setSelectedApartment(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Apartment"
                    placeholder="Choose an apartment (optional)"
                  />
                )}
                freeSolo
              />
            ) : (
              <TextField
                label="Apartment"
                value={selectedApartment || ''}
                onChange={(e) => setSelectedApartment(e.target.value || null)}
                fullWidth
                placeholder="Enter apartment (optional)"
                helperText={jobApartment ? `Available apartments: ${jobApartment}` : 'No apartments configured for this job'}
              />
            )}
            {fileObject && fileObject.type.startsWith('image/') && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDrawing}
                    onChange={(e) => setIsDrawing(e.target.checked)}
                  />
                }
                label="This is a drawing (will be converted to PDF)"
              />
            )}
            {fileObject && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Original filename: {fileObject.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  File size: {formatFileSize(fileObject.size)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !fileObject}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete File
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{fileToDelete?.originalName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.message.includes('Failed') || snackbar.message.includes('Invalid') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Comment Dialog */}
      {viewingFile && (
        <CommentDialog
          open={commentDialogOpen}
          onClose={() => {
            setCommentDialogOpen(false);
            setSelectedComment(null);
          }}
          comment={selectedComment}
          jobId={jobId}
          fileId={viewingFile.id}
          onUpdate={() => {
            // Trigger a refresh of comments in PDFViewerWithComments
            setCommentRefreshTrigger((prev) => prev + 1);
            setCommentDialogOpen(false);
            setSelectedComment(null);
          }}
          onDelete={() => {
            // Trigger a refresh of comments in PDFViewerWithComments
            setCommentRefreshTrigger((prev) => prev + 1);
            setCommentDialogOpen(false);
            setSelectedComment(null);
          }}
          currentUserId={currentUser?.id}
        />
      )}
    </Box>
  );
}
